import { DEFAULT_SIMULATION_DATE, TIME_FORMAT, MIN_TIME, MAX_TIME } from '../config/constants';
import i18n from '../i18n';

const DATE_ONLY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;
const CANONICAL_DATETIME_REGEX = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d{1,3}))?(?:Z|[+-]\d{2}:\d{2})?$/;

type ParsedWallTimeDateTime = {
  date: string;
  hour: string;
  minute: string;
  second: string;
  millisecond: string;
};

/**
 * Returns a default request time for simulation fallbacks.
 * Uses DEFAULT_SIMULATION_DATE + 08:00:00 in timezone-agnostic format.
 * Use when ride has no request_time (e.g., API fallback, programmatic creation).
 * @returns Full datetime string in format YYYY-MM-DDTHH:mm:ss.SSS
 */
export function getDefaultRequestTime(): string {
  return combineDateWithTime(DEFAULT_SIMULATION_DATE, '08:00');
}

/**
 * Build a Date anchored at a wall-time. Use this in place of
 * `new Date("YYYY-MM-DD")` — date-only ISO strings parse as UTC midnight and
 * roll back one day in any zone behind UTC (Americas, Brazil).
 */
export function createWallTimeDate(date: string, time: string = '00:00'): Date {
  return new Date(combineDateWithTime(date, time));
}

/**
 * Format a Date's local clock values as a wall-time string (no Z, no offset).
 * Companion to combineDateWithTime; use anywhere a Date must become a
 * wall-time request_time or shift time string.
 */
export function formatDateAsWallTime(date: Date): string {
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return `${y}-${mo}-${d}T${h}:${mi}:${s}.000`;
}

/**
 * Combines a date string with a time string to create a full datetime string
 * Uses timezone-agnostic approach where times are treated as local times
 * @param date - Date string in YYYY-MM-DD format
 * @param time - Time string in HH:mm format
 * @returns Full datetime string in ISO format (without timezone conversion)
 */
export function combineDateWithTime(date: string, time: string): string {
  if (!DATE_ONLY_REGEX.test(date)) {
    throw new Error(`Invalid date format: ${date}. Expected format: YYYY-MM-DD`);
  }
  if (!isValidTimeFormat(time)) {
    throw new Error(`Invalid time format: ${time}. Expected format: HH:mm`);
  }
  
  // Create datetime string without timezone conversion
  // This ensures 8am is always 8am regardless of user's timezone
  const [hours, minutes] = time.split(':').map(Number);
  const datetimeString = `${date}T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00.000`;
  
  return datetimeString;
}

/**
 * Parse simulation datetime deterministically without browser timezone interpretation.
 * Accepts "YYYY-MM-DDTHH:mm:ss(.SSS)" or "YYYY-MM-DD HH:mm:ss(.SSS)" (with optional timezone suffix).
 */
export function parseWallTimeDateTime(datetime: string): ParsedWallTimeDateTime {
  const input = (datetime || '').trim();
  const match = input.match(CANONICAL_DATETIME_REGEX);
  if (!match) {
    throw new Error(`Invalid datetime format: ${datetime}`);
  }

  const [, date, hour, minute, secondRaw, msRaw] = match;
  const second = (secondRaw || '00').padStart(2, '0');
  const millisecond = (msRaw || '000').padEnd(3, '0').slice(0, 3);
  return { date, hour, minute, second, millisecond };
}

/**
 * Normalizes supported datetime shapes to canonical simulation wall-time format.
 */
export function normalizeSimulationDateTime(datetime: string): string {
  const parsed = parseWallTimeDateTime(datetime);
  return `${parsed.date}T${parsed.hour}:${parsed.minute}:${parsed.second}.${parsed.millisecond}`;
}

/**
 * Extract service date (YYYY-MM-DD) from supported datetime/date strings deterministically.
 */
export function extractServiceDate(datetimeOrDate: string): string {
  const input = (datetimeOrDate || '').trim();
  if (DATE_ONLY_REGEX.test(input)) {
    return input;
  }
  return parseWallTimeDateTime(input).date;
}

/**
 * Returns the global default simulation service date.
 */
export function getDefaultSimulationServiceDate(): string {
  return DEFAULT_SIMULATION_DATE;
}

const MONTH_ABBREVIATIONS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/**
 * Render a wall-time date/datetime as "Mon D, YYYY" without going through
 * `new Date(...).toLocaleDateString(...)` — that path parses YYYY-MM-DD as
 * UTC midnight and shows the previous day in zones behind UTC.
 */
export function formatServiceDateLabel(datetimeOrDate: string): string {
  const isoDate = extractServiceDate(datetimeOrDate);
  const [year, month, day] = isoDate.split('-');
  const monthAbbr = MONTH_ABBREVIATIONS[Number(month) - 1] || month;
  return `${monthAbbr} ${Number(day)}, ${year}`;
}

/**
 * Extracts time portion from a datetime string
 * @param datetime - Full datetime string
 * @returns Time string in HH:mm format
 */
export function extractTimeFromDateTime(datetime: string): string {
  const parsed = parseWallTimeDateTime(datetime);
  return `${parsed.hour}:${parsed.minute}`;
}

/**
 * Shifts a wall-time datetime string by the given number of minutes. Stays in
 * wall-time (no UTC reinterpretation) by parsing fields, advancing the
 * minute-of-day, and rolling the calendar date forward/backward as needed.
 *
 * Use this for simulation request_time adjustments (e.g. "shift trips back 1
 * hour"). DO NOT use `new Date(iso).getTime() + minutes*60000` — that path
 * reinterprets the wall-time as UTC and produces the wrong clock value in any
 * zone that isn't UTC.
 *
 * @param datetime - Canonical wall-time string ("YYYY-MM-DDTHH:mm:ss(.SSS)?")
 * @param deltaMinutes - Positive to advance, negative to roll back.
 * @returns New wall-time string with the same fractional seconds preserved.
 */
export function shiftWallTimeByMinutes(datetime: string, deltaMinutes: number): string {
  const parsed = parseWallTimeDateTime(datetime);
  const [year, month, day] = parsed.date.split('-').map(Number);
  const totalMinutes =
    Number(parsed.hour) * 60 + Number(parsed.minute) + deltaMinutes;
  const dayDelta = Math.floor(totalMinutes / (24 * 60));
  const normalizedMinutes = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
  const newHour = Math.floor(normalizedMinutes / 60);
  const newMinute = normalizedMinutes % 60;
  // Use a wall-time anchored Date solely for date arithmetic — local timezone
  // construction at noon avoids DST edge cases when shifting the date forward.
  const anchor = new Date(year, month - 1, day + dayDelta, 12, 0, 0, 0);
  const y = anchor.getFullYear();
  const mo = String(anchor.getMonth() + 1).padStart(2, '0');
  const d = String(anchor.getDate()).padStart(2, '0');
  const hh = String(newHour).padStart(2, '0');
  const mm = String(newMinute).padStart(2, '0');
  return `${y}-${mo}-${d}T${hh}:${mm}:${parsed.second}.${parsed.millisecond}`;
}

/**
 * Validates if a time string is in the correct HH:mm format
 * @param time - Time string to validate
 * @returns True if valid, false otherwise
 */
export function isValidTimeFormat(time: string): boolean {
  if (typeof time !== 'string') return false;
  
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  if (!timeRegex.test(time)) return false;
  
  const [hours, minutes] = time.split(':').map(Number);
  return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
}

/**
 * Formats a time string for display purposes
 * @param time - Time string in HH:mm format
 * @returns Formatted time string
 */
export function formatTimeForDisplay(time: string): string {
  if (!isValidTimeFormat(time)) {
    throw new Error(`Invalid time format: ${time}`);
  }
  
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
}

/**
 * Parses various time input formats and returns standardized HH:mm format
 * @param time - Time input in various formats
 * @returns Standardized time string in HH:mm format
 */
export function parseTimeInput(time: string): string {
  if (!time || typeof time !== 'string') {
    throw new Error('Time input is required and must be a string');
  }
  
  // Handle HH:mm format
  if (isValidTimeFormat(time)) {
    return time;
  }
  
  // Handle HH:mm:ss format
  const timeWithSecondsRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;
  if (timeWithSecondsRegex.test(time)) {
    return time.substring(0, 5);
  }
  
  // Handle 12-hour format (e.g., "2:30 PM")
  const twelveHourRegex = /^([0-9]|1[0-2]):([0-5][0-9])\s*(AM|PM)$/i;
  const match = time.match(twelveHourRegex);
  if (match) {
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const period = match[3].toUpperCase();
    
    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }
    
    return `${hours.toString().padStart(2, '0')}:${minutes}`;
  }
  
  throw new Error(`Unsupported time format: ${time}. Supported formats: HH:mm, HH:mm:ss, or 12-hour format (e.g., "2:30 PM")`);
}

/**
 * Validates if a time range is valid (start time before end time)
 * @param startTime - Start time in HH:mm format
 * @param endTime - End time in HH:mm format
 * @returns True if valid range, false otherwise
 */
export function validateTimeRange(startTime: string, endTime: string): boolean {
  if (!isValidTimeFormat(startTime) || !isValidTimeFormat(endTime)) {
    return false;
  }
  
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const [endHours, endMinutes] = endTime.split(':').map(Number);
  
  const startTotalMinutes = startHours * 60 + startMinutes;
  const endTotalMinutes = endHours * 60 + endMinutes;
  
  return startTotalMinutes < endTotalMinutes;
}

/**
 * Checks if two time ranges overlap
 * @param range1Start - First range start time
 * @param range1End - First range end time
 * @param range2Start - Second range start time
 * @param range2End - Second range end time
 * @returns True if ranges overlap, false otherwise
 */
export function doTimeRangesOverlap(
  range1Start: string,
  range1End: string,
  range2Start: string,
  range2End: string
): boolean {
  if (!validateTimeRange(range1Start, range1End) || !validateTimeRange(range2Start, range2End)) {
    return false;
  }
  
  const [start1Hours, start1Minutes] = range1Start.split(':').map(Number);
  const [end1Hours, end1Minutes] = range1End.split(':').map(Number);
  const [start2Hours, start2Minutes] = range2Start.split(':').map(Number);
  const [end2Hours, end2Minutes] = range2End.split(':').map(Number);
  
  const start1Total = start1Hours * 60 + start1Minutes;
  const end1Total = end1Hours * 60 + end1Minutes;
  const start2Total = start2Hours * 60 + start2Minutes;
  const end2Total = end2Hours * 60 + end2Minutes;
  
  return start1Total < end2Total && start2Total < end1Total;
}

/**
 * Converts a time string to total minutes for comparison
 * @param time - Time string in HH:mm format
 * @returns Total minutes since midnight
 */
export function timeToMinutes(time: string): number {
  if (!isValidTimeFormat(time)) {
    throw new Error(`Invalid time format: ${time}`);
  }
  
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Converts total minutes to time string
 * @param minutes - Total minutes since midnight (can exceed 24 hours for GTFS times)
 * @returns Time string in HH:mm format (normalized to 24-hour format, e.g., 25:30 becomes 01:30)
 */
export function minutesToTime(minutes: number): string {
  if (minutes < 0) {
    throw new Error(`Invalid minutes: ${minutes}. Must be non-negative`);
  }
  
  // Normalize minutes to 24-hour format (GTFS allows times > 24 hours)
  // e.g., 1442 minutes (24:02) becomes 2 minutes (00:02)
  const normalizedMinutes = minutes % (24 * 60);
  
  const hours = Math.floor(normalizedMinutes / 60);
  const mins = normalizedMinutes % 60;
  
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

/**
 * Creates a datetime string using the default simulation date
 * Handles overnight shifts by adjusting the date when end time is before start time
 * @param time - Time string in HH:mm format
 * @param isEndTime - Whether this is an end time (for overnight shift handling)
 * @param startTime - The start time for comparison (required when isEndTime is true)
 * @returns Full datetime string using default date
 */
export function createDefaultDateTime(time: string, isEndTime: boolean = false, startTime?: string): string {
  if (isEndTime && startTime) {
    // Check if this is an overnight shift (end time is before start time)
    if (validateTimeRange(startTime, time)) {
      // Normal shift - same day
      return combineDateWithTime(DEFAULT_SIMULATION_DATE, time);
    } else {
      // Overnight shift - end time is on the next day
      const [yearStr, monthStr, dayStr] = DEFAULT_SIMULATION_DATE.split('-');
      const nextDay = new Date(Number(yearStr), Number(monthStr) - 1, Number(dayStr) + 1);
      const nextDayString = `${nextDay.getFullYear()}-${String(nextDay.getMonth() + 1).padStart(2, '0')}-${String(nextDay.getDate()).padStart(2, '0')}`;
      return combineDateWithTime(nextDayString, time);
    }
  }
  
  // Default behavior for start times or when no comparison needed
  return combineDateWithTime(DEFAULT_SIMULATION_DATE, time);
}

/**
 * Extracts time from datetime and creates new datetime with default date
 * @param datetime - Original datetime string
 * @returns New datetime string with default date
 */
export function convertToDefaultDate(datetime: string): string {
  const parsed = parseWallTimeDateTime(datetime);
  return `${DEFAULT_SIMULATION_DATE}T${parsed.hour}:${parsed.minute}:${parsed.second}.${parsed.millisecond}`;
}

/**
 * Validates if a datetime string uses the default date
 * @param datetime - Datetime string to check
 * @returns True if uses default date, false otherwise
 */
export function usesDefaultDate(datetime: string): boolean {
  try {
    return extractServiceDate(datetime) === DEFAULT_SIMULATION_DATE;
  } catch {
    return false;
  }
}

/**
 * Converts a local time to datetime string for backend communication
 * Uses timezone-agnostic approach - no timezone conversion
 * @param date - Date string in YYYY-MM-DD format
 * @param time - Time string in HH:mm format
 * @returns Datetime string in ISO format
 */
export function localTimeToUTC(date: string, time: string): string {
  return combineDateWithTime(date, time);
}

/**
 * Converts a datetime string to local time for display
 * Uses timezone-agnostic approach - no timezone conversion
 * @param utcDateTime - Datetime string
 * @returns Local time string in HH:mm format
 */
export function utcToLocalTime(utcDateTime: string): string {
  return extractTimeFromDateTime(utcDateTime);
}

/**
 * Validates that a datetime string is in proper format
 * @param datetime - Datetime string to validate
 * @returns True if valid format, false otherwise
 */
export function isValidUTCDatetime(datetime: string): boolean {
  try {
    parseWallTimeDateTime(datetime);
    return true;
  } catch {
    return false;
  }
}

/**
 * Creates a datetime string for display purposes
 * Uses timezone-agnostic approach - no timezone conversion
 * @param utcDateTime - Datetime string from backend
 * @returns Local datetime string for display
 */
export function formatDateTimeForDisplay(utcDateTime: string): string {
  try {
    const parsed = parseWallTimeDateTime(utcDateTime);
    return `${parsed.date} ${parsed.hour}:${parsed.minute}`;
  } catch (error) {
    console.error('Error formatting datetime for display:', error);
    return utcDateTime; // Fallback to original string
  }
} 

/**
 * Formats announcement publish timestamps using the effective timezone.
 * Falls back to the original value when parsing or formatting fails.
 */
export function formatAnnouncementDateWithTimezone(utcDateTime: string, effectiveTimezone: string): string {
  try {
    const date = new Date(utcDateTime);
    if (isNaN(date.getTime())) {
      return utcDateTime;
    }

    const locale = i18n.language || 'en-US';
    return new Intl.DateTimeFormat(locale, {
      timeZone: effectiveTimezone,
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    }).format(date);
  } catch (error) {
    console.warn('[dateTimeUtils] Failed to format announcement date with timezone', {
      effectiveTimezone,
      error,
    });
    return utcDateTime;
  }
}
